import { useState } from 'react'
import { eq, useLiveQuery } from '@tanstack/react-db'

import type {
  CalloutLogWorkNotePatch,
  CalloutLogWorkPhotoNoteInput,
  CalloutLogWorkScreenProps,
} from '@/screens/CalloutLogWorkScreen'
import { useDb } from '@/lib/dbContext'
import { useAuth } from '@/lib/auth'
import { formatPocketBaseError } from '@/lib/pocketbaseErrors'
import { formatRelativeDateTime } from '@/lib/relativeDateTime'
import { Sentry, withSpan } from '@/lib/sentry'
import { looksLikePublicId } from '@/lib/public-id/routeLookup'
import { captureLegacyIdRoute } from '@/lib/public-id/legacyRouteSentry'
import type { Customer, Job, JobAssignment, Note, StatusChangeLog } from '@/lib/schemas/db'
import {
  getAllowedJobStatusTransitions,
  jobStatusRequiresReason,
  type JobStatus,
} from '@/db/domain/jobStatus'
import type { User as UserModel } from '@/db/domain/models'
import { getCurrentAssignment } from '@/lib/jobAssignments'
import { buildServiceFeed } from '@/screens/serviceFeed'
import { useEnsureServiceLoaded } from '@/screens/useEnsureServiceLoaded'

export type JobLogWorkStatus =
  | 'NewJob'
  | 'ReadyToWork'
  | 'InProgress'
  | 'Blocked'
  | 'WaitingOnParts'
  | 'ReadyForCollection'
  | 'ReadyForCollectionUnsuccessful'
  | 'ReadyToInvoice'
  | 'Invoiced'
  | 'Cancelled'

export type JobLogWorkState =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'not_found' }
  | {
      kind: 'ready'
      screenProps: CalloutLogWorkScreenProps
      photoNoteError: string | null
      photoNoteRollbackId: string | null
      clearPhotoNoteError: () => void
    }

function mapJobStatus(status: Job['status']): JobLogWorkStatus {
  switch (status) {
    case 'NewJob':
    case 'ReadyToWork':
    case 'InProgress':
    case 'Blocked':
    case 'WaitingOnParts':
    case 'ReadyForCollection':
    case 'ReadyForCollectionUnsuccessful':
    case 'ReadyToInvoice':
    case 'Invoiced':
    case 'Cancelled':
      return status
    default:
      return 'NewJob'
  }
}

export function useJobLogWork(jobIdentity: string): JobLogWorkState {
  const db = useDb()
  const { user } = useAuth()
  const [photoNoteError, setPhotoNoteError] = useState<string | null>(null)
  const [photoNoteRollbackId, setPhotoNoteRollbackId] = useState<string | null>(null)
  const tryPublicId = looksLikePublicId(jobIdentity)

  const jobByIdResult = useLiveQuery(
    (q) =>
      jobIdentity
        ? q
            .from({ jobs: db.jobsCollection })
            .where(({ jobs }) => eq(jobs.id, jobIdentity))
            .findOne()
        : null,
    [jobIdentity],
  )

  const jobByPublicIdResult = useLiveQuery(
    (q) =>
      jobIdentity && tryPublicId
        ? q
            .from({ jobs: db.jobsCollection })
            .where(({ jobs }) => eq(jobs.publicId, jobIdentity))
            .findOne()
        : null,
    [jobIdentity, tryPublicId],
  )

  const job =
    (jobByIdResult.data as Job | undefined) ??
    (jobByPublicIdResult.data as Job | undefined)
  const serviceId = job?.id ?? jobIdentity

  if (!tryPublicId && job && (jobByIdResult.data as Job | undefined)) {
    captureLegacyIdRoute('job', jobIdentity)
  }

  const customerResult = useLiveQuery(
    (q) => {
      if (!job?.customerId) return null
      return q
        .from({ customers: db.customersCollection })
        .where(({ customers }) => eq(customers.id, job.customerId))
        .findOne()
    },
    [job?.customerId],
  )

  const notesResult = useLiveQuery(
    (q) =>
      serviceId
        ? q
            .from({ notes: db.jobNotesCollection })
            .where(({ notes }) => eq(notes.serviceId, serviceId))
            .orderBy(({ notes }) => notes.createdAt)
        : null,
    [serviceId],
  )

  const statusChangesResult = useLiveQuery(
    (q) =>
      serviceId
        ? q
            .from({ statusChanges: db.jobStatusChangesCollection })
            .where(({ statusChanges }) => eq(statusChanges.serviceId, serviceId))
            .orderBy(({ statusChanges }) => statusChanges.createdAt)
        : null,
    [serviceId],
  )

  const usersResult = useLiveQuery((q) => q.from({ users: db.usersCollection }))
  const assignmentsResult = useLiveQuery(
    (q) =>
      serviceId
        ? q
            .from({ assignments: db.jobAssignmentsCollection })
            .where(({ assignments }) => eq(assignments.jobId, serviceId))
        : null,
    [serviceId],
  )

  // Archived (Invoiced/Cancelled) jobs aren't in the live sync; pull one in
  // on demand so the locked Log Work tab still works.
  const recordQueriesSettled =
    !jobByIdResult.isLoading &&
    !jobByPublicIdResult.isLoading &&
    !jobByIdResult.isError &&
    !jobByPublicIdResult.isError
  const { hydrating } = useEnsureServiceLoaded(
    'job',
    jobIdentity,
    recordQueriesSettled && !job,
  )

  if (
    hydrating ||
    jobByIdResult.isLoading ||
    jobByPublicIdResult.isLoading ||
    customerResult.isLoading ||
    notesResult.isLoading ||
    statusChangesResult.isLoading ||
    usersResult.isLoading ||
    assignmentsResult.isLoading
  ) return { kind: 'loading' }

  if (
    jobByIdResult.isError ||
    jobByPublicIdResult.isError ||
    customerResult.isError ||
    notesResult.isError ||
    statusChangesResult.isError ||
    usersResult.isError ||
    assignmentsResult.isError
  ) return { kind: 'error' }

  if (!job) return { kind: 'not_found' }

  const linkedCustomer = customerResult.data as Customer | undefined
  const linkedCustomerName = linkedCustomer
    ? linkedCustomer.externalId
      ? `${linkedCustomer.fullName} – ${linkedCustomer.externalId}`
      : linkedCustomer.fullName
    : 'Unknown customer'

  const userList = ((usersResult.data ?? []) as UserModel[])
    .map((entry) => ({ id: entry.id, name: entry.name || entry.email }))
    .sort((a, b) => a.name.localeCompare(b.name))
  const usersById = new Map(
    ((usersResult.data ?? []) as UserModel[]).map((entry) => [entry.id, entry]),
  )
  const currentAssignment = getCurrentAssignment(
    (assignmentsResult.data ?? []) as JobAssignment[],
    usersById,
  )

  const notes = buildServiceFeed(
    notesResult.data as Note[] | undefined,
    statusChangesResult.data as StatusChangeLog[] | undefined,
    db.getAuthorDisplayName,
  )

  const handleAddTextNote = (text: string) => {
    if (!text.trim()) return
    return Sentry.startSpan(
      { op: 'job.note.add', name: 'Add text note', attributes: { serviceId: job.id } },
      () => {
        const note = db.appendJobLog(job.id, { text })
        return { id: note.id, createdAt: note.createdAt }
      },
    )
  }

  const handleAddPhotoNote = (input: CalloutLogWorkPhotoNoteInput) => {
    // The caption is optional — a photo with no words is still a note.
    const caption = input.caption.trim()
    setPhotoNoteError(null)
    setPhotoNoteRollbackId(null)

    const { log, transaction } = Sentry.startSpan(
      { op: 'job.note.add_photo', name: 'Add photo note', attributes: { serviceId: job.id } },
      () => db.appendJobLogWithTransaction(job.id, {
        caption,
        photoUrl: input.photoUrls[0],
        photoUrls: input.photoUrls,
        photoFiles: input.photoFiles,
      }),
    )

    transaction?.isPersisted?.promise
      ?.then(() => setPhotoNoteError(null))
      .catch((error) => {
        setPhotoNoteError(
          formatPocketBaseError(error, {
            fallbackMessage: 'Unable to save photo note. Please try again.',
            fieldLabels: { photo: 'Photo' },
          }),
        )
        setPhotoNoteRollbackId(log.id)
        try {
          if (db.jobNotesCollection.has?.(log.id)) {
            // Hard delete: the upload failed, so this note never became
            // part of the record and should leave no tombstone.
            db.discardJobLog(job.id, log.id)
          }
        } catch {}
      })

    return { id: log.id, createdAt: log.createdAt }
  }

  const handleEditNote = (id: string, patch: CalloutLogWorkNotePatch) => {
    db.updateJobLog(job.id, id, patch)
  }

  const handleDeleteNote = (id: string) => {
    db.deleteJobLog(job.id, id)
  }

  const handleAssign = async (userId: string) =>
    withSpan(
      { op: 'job.assign', name: 'Assign job from log work', attributes: { serviceId: job.id, userId } },
      () => db.assignJobAndWait(job.id, userId),
    )

  const handleChangeStatus = async (
    status: JobStatus,
    reason?: string,
    occurredAt?: string,
  ) => {
    const normalizedReason = reason?.trim() || undefined
    if (
      (status === 'Cancelled' || jobStatusRequiresReason(status)) &&
      !normalizedReason
    ) {
      throw new Error(`Reason is required when moving a job to ${status}`)
    }
    return withSpan(
      { op: 'job.status.change', name: 'Change job status from log work', attributes: { serviceId: job.id, nextStatus: status } },
      () =>
        db.updateJobAndWait?.(job.id, {
          status,
          statusReason: normalizedReason,
          statusOccurredAt: occurredAt,
        }),
    )
  }

  return {
    kind: 'ready',
    photoNoteError,
    photoNoteRollbackId,
    clearPhotoNoteError: () => {
      setPhotoNoteError(null)
      setPhotoNoteRollbackId(null)
    },
    screenProps: {
      id: job.id,
      publicId: job.publicId,
      resourceLabel: 'Job',
      customerName: linkedCustomerName,
      customerId: job.customerId,
      customerPublicId: linkedCustomer?.publicId,
      issueSummary: job.fault ?? '',
      status: mapJobStatus(job.status),
      scheduledAt: formatRelativeDateTime(job.createdAt),
      address: '',
      contactName: '',
      contactPhone: '',
      billables: [],
      notes,
      statusChanges: statusChangesResult.data as StatusChangeLog[] | undefined,
      getAuthorDisplayName: db.getAuthorDisplayName,
      todos: [],
      newNoteAuthorName: user?.name ?? '',
      onAddTextNote: handleAddTextNote,
      onAddPhotoNote: handleAddPhotoNote,
      photoNoteError,
      photoNoteRollbackId,
      onClearPhotoNoteError: () => {
        setPhotoNoteError(null)
        setPhotoNoteRollbackId(null)
      },
      onEditNote: handleEditNote,
      onDeleteNote: handleDeleteNote,
      onCancelCallout: async (reason?: string) => {
        const normalizedReason = reason?.trim() ?? ''
        if (!normalizedReason) throw new Error('Reason is required when cancelling a job')
        return withSpan(
          { op: 'job.status.change', name: 'Cancel job', attributes: { serviceId: job.id, nextStatus: 'Cancelled' } },
          () => db.updateJobAndWait?.(job.id, { status: 'Cancelled', statusReason: normalizedReason }),
        )
      },
      availableStatusTransitions: getAllowedJobStatusTransitions(job.status),
      onChangeStatus: (status, reason, occurredAt) =>
        handleChangeStatus(status as JobStatus, reason, occurredAt),
      users: userList,
      assignedUserId: currentAssignment?.assignment.userId,
      assignedUserName: currentAssignment
        ? currentAssignment.user?.name || currentAssignment.user?.email
        : undefined,
      onAssign: handleAssign,
    },
  }
}
